import { FC, PropsWithChildren, useMemo } from 'react';
import { css } from '@emotion/react';
import { TaskStatus } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { type Task } from '@frontend/api-call-intel';
import { DataAnalystRolePromo } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Accordion, NotificationBadge } from '@frontend/design-system';
import { CallTasks, CallTasksProps } from '../call-tasks';

interface TaskAccordionNamespace extends FC<PropsWithChildren> {
  Header: typeof TaskAccordionHeader;
  Body: typeof TaskAccordionBody;
}

export const TaskAccordion: TaskAccordionNamespace = ({ children }) => {
  return (
    <Accordion>
      <Accordion.Item value='taskAccordion'>{children}</Accordion.Item>
    </Accordion>
  );
};

const TaskAccordionHeader: FC<PropsWithChildren<{ title?: string; tasks: Task[]; iconName?: IconName }>> = ({
  title,
  tasks,
  children,
  iconName,
}) => {
  const { t } = useTranslation('call-intel-core');

  const incompleteTasks = useMemo(
    () =>
      tasks.filter(({ status }) => status === TaskStatus.STATUS_TODO || status === TaskStatus.STATUS_IN_PROGRESS) || [],
    [tasks]
  );

  const headerTitle = title || t('Tasks');

  return (
    <Accordion.Header title={iconName ? undefined : headerTitle}>
      {iconName && <Accordion.Header.Icon icon={iconName} title={headerTitle} />}
      <div css={styles.headerIcon}>
        {incompleteTasks.length > 0 && (
          <NotificationBadge css={styles.notificationBadge} truncateCount>
            {incompleteTasks?.length}
          </NotificationBadge>
        )}
        {children}
      </div>
    </Accordion.Header>
  );
};

type TaskAccordionBodyProps = CallTasksProps;

const TaskAccordionBody: FC<TaskAccordionBodyProps> = (props) => {
  const locationIds = props.call.locationId ? [props.call.locationId] : [];

  return (
    <Accordion.Body css={styles.accordionBody}>
      <CallTasks {...props} />
      <DataAnalystRolePromo
        locationIds={locationIds}
        source='call-insight-tasks'
        styles={{ padding: theme.spacing(2), borderTop: `1px solid ${theme.colors.neutral20}` }}
      />
    </Accordion.Body>
  );
};

const styles = {
  headerIcon: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-left: ${theme.spacing(1)};
  `,
  notificationBadge: css`
    height: ${theme.spacing(2)};
    width: ${theme.spacing(2)};
    padding: ${theme.spacing(0, 0.5)};
    border-radius: ${theme.borderRadius.full};
  `,
  accordionBody: css`
    padding: ${theme.spacing(0, 0)};
  `,
};

TaskAccordion.Header = TaskAccordionHeader;
TaskAccordion.Body = TaskAccordionBody;
